import { Link, useParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import Breadcrumb from "@/components/feature/Breadcrumb";
import MovieCard from "@/components/feature/MovieCard";
import { getMovieById, movies } from "@/mocks/movies";
import { categories } from "@/mocks/categories";
import { useFavorites } from "@/hooks/useFavorites";
import { useSeo } from "@/hooks/useSeo";

const genreName = (key: string) =>
  categories.find((c) => c.key === key)?.name ?? key;

export default function DetailPage() {
  const { id = "" } = useParams();
  const movie = getMovieById(id);
  const { isFavorite, toggleFavorite } = useFavorites();

  useSeo({
    title: movie
      ? `${movie.title} - 详细影评与资讯 - 欧美大片影视网`
      : "影片未找到 - 欧美大片影视网",
    description: movie
      ? `${movie.title}（${movie.titleEn}）由${movie.director}执导，${movie.cast.join("、")}主演，${movie.year}年上映。${movie.reviewExcerpt}`
      : "你访问的影片不存在或已下架，返回首页浏览更多欧美电影。",
    keywords: movie
      ? `${movie.title},${movie.titleEn},${movie.tags.join(",")},欧美电影,影评`
      : "欧美电影,影评",
  });

  if (!movie) {
    return (
      <SiteLayout>
        <div className="w-full px-4 md:px-8 lg:px-12 py-20 md:py-28 text-center">
          <div className="w-16 h-16 mx-auto flex items-center justify-center rounded-full bg-background-200 text-foreground-400 mb-4">
            <i className="ri-movie-line text-3xl" />
          </div>
          <h1 className="font-heading font-extrabold text-2xl text-foreground-900 mb-3">
            影片未找到
          </h1>
          <p className="text-sm text-foreground-500 mb-6">
            你访问的影片可能不存在或已下架，回到首页看看其他精彩影片吧。
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-md bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap"
          >
            <i className="ri-home-5-line" /> 返回首页
          </Link>
        </div>
      </SiteLayout>
    );
  }

  const favorited = isFavorite(movie.id);
  const related = movies
    .filter((m) => m.id !== movie.id && m.genres.some((g) => movie.genres.includes(g)))
    .sort((a, b) => b.rating - a.rating)
    .slice(0, 4);

  return (
    <SiteLayout>
      {/* hero */}
      <section className="relative w-full bg-primary-950">
        <div className="absolute inset-0 overflow-hidden">
          <img
            src={movie.backdrop}
            alt={`${movie.title} 剧照`}
            className="w-full h-full object-cover object-top opacity-40"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-primary-950 via-primary-950/80 to-primary-950/60" />
        </div>

        <div className="relative w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
          <Breadcrumb
            items={[
              { label: "首页", to: "/" },
              { label: `${genreName(movie.genres[0])}电影`, to: `/category/${movie.genres[0]}` },
              { label: movie.title },
            ]}
          />

          <div className="mt-6 md:mt-8 grid grid-cols-1 lg:grid-cols-12 gap-6 md:gap-10">
            <div className="lg:col-span-3">
              <div className="w-40 sm:w-48 lg:w-full max-w-[260px] aspect-[2/3] rounded-lg overflow-hidden bg-background-200">
                <img
                  src={movie.poster}
                  alt={`${movie.title} 海报`}
                  title={`${movie.title} ${movie.titleEn} 欧美电影海报`}
                  className="w-full h-full object-top object-cover"
                />
              </div>
            </div>

            <div className="lg:col-span-9 text-background-50">
              <div className="flex flex-wrap items-center gap-2 mb-3">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${g}`}
                    className="px-3 py-1 rounded-full bg-accent-500/90 text-primary-950 text-xs font-semibold hover:bg-accent-400 transition-colors cursor-pointer"
                  >
                    {genreName(g)}
                  </Link>
                ))}
                <span className="px-3 py-1 rounded-full bg-background-50/10 text-background-100 text-xs">
                  {movie.year} 年
                </span>
                <span className="px-3 py-1 rounded-full bg-background-50/10 text-background-100 text-xs">
                  {movie.region}
                </span>
              </div>

              <h1 className="font-heading font-extrabold text-3xl md:text-4xl lg:text-5xl leading-tight mb-2">
                {movie.title}
              </h1>
              <p className="font-label text-sm tracking-wide text-background-300/80 uppercase mb-5">
                {movie.titleEn}
              </p>

              <div className="flex flex-wrap items-center gap-6 mb-6">
                <div className="flex items-center gap-2">
                  <span className="font-heading font-extrabold text-3xl md:text-4xl text-accent-400">
                    {movie.rating.toFixed(1)}
                  </span>
                  <div className="text-xs text-background-300/80 leading-tight">
                    <div className="flex text-accent-400">
                      {Array.from({ length: 5 }).map((_, i) => (
                        <i
                          key={i}
                          className={
                            i < Math.round(movie.rating / 2)
                              ? "ri-star-fill"
                              : "ri-star-line"
                          }
                        />
                      ))}
                    </div>
                    <span>{movie.votes.toLocaleString()} 人评价</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-sm text-background-200">
                  <i className="ri-time-line text-accent-400" />
                  {movie.duration} 分钟
                </div>
              </div>

              <div className="flex flex-wrap gap-3">
                <button
                  type="button"
                  onClick={() => toggleFavorite(movie.id)}
                  className={`inline-flex items-center gap-2 px-6 py-3 rounded-md font-medium text-sm transition-colors cursor-pointer whitespace-nowrap ${
                    favorited
                      ? "bg-accent-500 text-primary-950"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={favorited ? "ri-heart-3-fill" : "ri-heart-3-line"} />
                  {favorited ? "已收藏" : "收藏电影"}
                </button>
                <Link
                  to={`/category/${movie.genres[0]}`}
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-md border border-background-50/30 text-background-50 font-medium text-sm hover:bg-background-50/10 transition-colors cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-apps-2-line" /> 同类影片
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* content */}
      <section className="w-full px-4 md:px-8 lg:px-12 py-8 md:py-12">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 md:gap-10">
          {/* main */}
          <article className="lg:col-span-8">
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-4">
              剧情简介
            </h2>
            <p className="text-sm md:text-base text-foreground-600 leading-loose mb-8">
              {movie.synopsis}
            </p>

            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950 mb-4">
              详细影评
            </h2>
            <div className="space-y-4">
              {movie.review.map((para, i) => (
                <p
                  key={i}
                  className="text-sm md:text-base text-foreground-600 leading-loose"
                >
                  {para}
                </p>
              ))}
            </div>

            <div className="mt-8 flex flex-wrap items-center gap-2">
              {movie.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-800 text-xs font-medium"
                >
                  #{tag}
                </span>
              ))}
            </div>

            <div className="mt-8 p-4 md:p-5 rounded-lg bg-background-100 border border-background-200 text-xs md:text-sm text-foreground-500 leading-relaxed">
              <i className="ri-information-line text-accent-600 mr-1" />
              本页影片信息与影评均为本站示范内容，人物、评分与剧情均为虚构，仅用于展示网站功能，不代表任何真实影视作品。
            </div>
          </article>

          {/* sidebar */}
          <aside className="lg:col-span-4">
            <div className="rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base text-foreground-900 mb-4">
                影片资料
              </h2>
              <dl className="space-y-3 text-sm">
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">导演</dt>
                  <dd className="text-foreground-800">{movie.director}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">主演</dt>
                  <dd className="text-foreground-800">{movie.cast.join("、")}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">上映年份</dt>
                  <dd className="text-foreground-800">{movie.year} 年</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">类型</dt>
                  <dd className="text-foreground-800">
                    {movie.genres.map(genreName).join(" / ")}
                  </dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">片长</dt>
                  <dd className="text-foreground-800">{movie.duration} 分钟</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">地区</dt>
                  <dd className="text-foreground-800">{movie.region}</dd>
                </div>
                <div className="flex gap-3">
                  <dt className="w-20 shrink-0 text-foreground-400">评分</dt>
                  <dd className="text-accent-700 font-semibold">
                    {movie.rating.toFixed(1)} / 10
                  </dd>
                </div>
              </dl>
            </div>

            <div className="mt-5 rounded-lg bg-background-100 border border-background-200 p-5 md:p-6">
              <h2 className="font-heading font-bold text-base text-foreground-900 mb-3">
                所属分类
              </h2>
              <div className="flex flex-wrap gap-2">
                {movie.genres.map((g) => (
                  <Link
                    key={g}
                    to={`/category/${g}`}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors cursor-pointer"
                  >
                    <i className="ri-price-tag-3-line text-accent-600" />
                    {genreName(g)}电影
                  </Link>
                ))}
              </div>
            </div>
          </aside>
        </div>
      </section>

      {/* related */}
      <section className="w-full bg-background-100 border-t border-background-200">
        <div className="w-full px-4 md:px-8 lg:px-12 py-10 md:py-14">
          <div className="flex items-center gap-3 mb-6">
            <span className="w-1 h-7 rounded-full bg-primary-500" />
            <h2 className="font-heading font-extrabold text-xl md:text-2xl text-foreground-950">
              相关推荐
            </h2>
          </div>
          {related.length ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-4 md:gap-5">
              {related.map((m) => (
                <MovieCard
                  key={m.id}
                  movie={m}
                  isFavorite={isFavorite(m.id)}
                  onToggleFavorite={toggleFavorite}
                />
              ))}
            </div>
          ) : (
            <p className="text-sm text-foreground-500">
              暂无相关推荐，去
              <Link to="/" className="text-primary-600 mx-1">
                首页
              </Link>
              看看吧。
            </p>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}